import { supabase } from './supabaseClient'
import { companiesApi, recipientsApi, reportsApi } from './api'
import { useState, useEffect } from "react";

// ─── THEME ────────────────────────────────────────────────────────────────────
const T = {
  navy: "#0e1320", navyMid: "#161d2e", navyLight: "#1e2a40",
  navyBorder: "#2a3650", accent: "#4f8ef7", accentHover: "#3a7ae8",
  white: "#ffffff", offWhite: "#f4f6fb", border: "#e4e9f2",
  textPrimary: "#0e1320", textSecondary: "#5a6680", textMuted: "#8f9ab5",
  green: "#22c55e", greenBg: "#dcfce7", greenText: "#15803d",
  amber: "#f59e0b", amberBg: "#fef3c7", amberText: "#92400e",
  blue: "#3b82f6", blueBg: "#eff6ff", blueText: "#1d4ed8", red: "#ef4444",
};

// ─── PRIMITIVES ───────────────────────────────────────────────────────────────
function Logo({ inverted }) {
  return (
    <span style={{
      fontSize: 20, fontFamily: "'Fraunces', serif", fontWeight: 900,
      color: inverted ? T.white : T.navy, letterSpacing: "-0.5px", lineHeight: 1,
    }}>Aquity AI</span>
  );
}

function Btn({ children, variant = "primary", onClick, small, disabled }) {
  const base = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    padding: small ? "7px 14px" : "10px 20px",
    borderRadius: 9, fontSize: small ? 12.5 : 13.5, fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif", cursor: disabled ? "not-allowed" : "pointer",
    letterSpacing: 0.1, transition: "all 0.15s", border: "none",
    opacity: disabled ? 0.5 : 1, whiteSpace: "nowrap",
  };
  const variants = {
    primary: { background: T.navy, color: T.white },
    outline: { background: "transparent", color: T.textPrimary, border: `1.5px solid ${T.border}` },
    ghost: { background: "transparent", color: T.red, padding: 0, fontSize: 12.5, fontWeight: 600 },
    accent: { background: T.accent, color: T.white },
  };
  return (
    <button disabled={disabled} onClick={onClick} style={{ ...base, ...variants[variant] }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.opacity = "0.8"; }}
      onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
    >{children}</button>
  );
}

function Input({ placeholder, value, onChange, type = "text" }) {
  return (
    <input type={type} placeholder={placeholder} value={value} onChange={onChange}
      style={{
        width: "100%", padding: "10px 14px", border: `1.5px solid ${T.border}`,
        borderRadius: 9, fontSize: 13.5, fontFamily: "'DM Sans', sans-serif",
        color: T.textPrimary, outline: "none", boxSizing: "border-box",
        background: T.offWhite, transition: "border 0.15s",
      }}
      onFocus={e => e.target.style.borderColor = T.accent}
      onBlur={e => e.target.style.borderColor = T.border}
    />
  );
}

function Td({ children, muted, center }) {
  return (
    <td style={{
      padding: "12px 16px", fontSize: 13.5,
      color: muted ? T.textMuted : T.textPrimary,
      fontFamily: "'DM Sans', sans-serif",
      textAlign: center ? "center" : "left",
    }}>{children}</td>
  );
}

function Table({ headers, rows, renderRow, emptyMsg }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'DM Sans', sans-serif" }}>
        <thead>
          <tr style={{ background: T.offWhite }}>
            {headers.map((h, i) => (
              <th key={i} style={{
                textAlign: "left", padding: "9px 16px", fontSize: 11,
                fontWeight: 700, color: T.textMuted, textTransform: "uppercase",
                letterSpacing: 0.9, borderBottom: `1px solid ${T.border}`,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0
            ? <tr><td colSpan={headers.length} style={{ padding: "32px 16px", textAlign: "center", color: T.textMuted, fontSize: 13.5, fontFamily: "'DM Sans', sans-serif" }}>{emptyMsg || "No records found"}</td></tr>
            : rows.map((row, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${T.border}`, transition: "background 0.1s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >{renderRow(row, i)}</tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function Avatar({ name, round }) {
  return (
    <div style={{
      width: 30, height: 30, borderRadius: round ? "50%" : 7,
      background: round ? "linear-gradient(135deg,#4f8ef7,#7c5cbf)" : T.navy,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 12, fontWeight: 700, color: T.white, fontFamily: "'DM Sans', sans-serif", flexShrink: 0,
    }}>{name[0].toUpperCase()}</div>
  );
}


// ─── CSV SECURITY ─────────────────────────────────────────────────────────────
const CSV_CONFIG = {
  maxSizeBytes: 2 * 1024 * 1024,  // 2MB
  maxRows: 500,
  allowedMimeTypes: ["text/csv", "text/plain", "application/csv", "application/vnd.ms-excel"],
  allowedExtensions: [".csv"],
};

// Patterns that indicate injection or malicious content
const INJECTION_PATTERNS = [
  /^[=+\-@]/, // CSV formula injection
  /<script/i,  // script tags
  /javascript:/i, // javascript protocol
  /on\w+=/i,  // event handlers
  /\bDROP\b|\bDELETE\b|\bINSERT\b|\bUPDATE\b|\bSELECT\b|\bEXEC\b|\bUNION\b/i, // SQL injection
];

const EMAIL_REGEX = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

function validateCSV(file, options = {}) {
  const { validateEmails = false } = options;
  const errors = [];

  // 1 — File extension check
  const ext = "." + file.name.split(".").pop().toLowerCase();
  if (!CSV_CONFIG.allowedExtensions.includes(ext)) {
    return { valid: false, error: `Invalid file type. Only .csv files are allowed.` };
  }

  // 2 — MIME type check (browsers may report different types)
  if (file.type && file.type !== "" && !CSV_CONFIG.allowedMimeTypes.includes(file.type)) {
    return { valid: false, error: `Invalid file format. Please upload a valid CSV file.` };
  }

  // 3 — File size check
  if (file.size > CSV_CONFIG.maxSizeBytes) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return { valid: false, error: `File too large (${sizeMB}MB). Maximum allowed size is 2MB.` };
  }

  return { valid: true };
}

function validateCSVContent(lines, options = {}) {
  const { validateEmails = false } = options;

  // 4 — Row limit check
  if (lines.length > CSV_CONFIG.maxRows) {
    return { valid: false, error: `Too many rows (${lines.length}). Maximum allowed is ${CSV_CONFIG.maxRows} rows per import.` };
  }

  // 5 — Scan each cell for injection and malicious content
  for (let i = 0; i < lines.length; i++) {
    const cells = lines[i].split(",").map(c => c.trim().replace(/^"|"$/g, ""));
    for (const cell of cells) {
      if (!cell) continue;
      for (const pattern of INJECTION_PATTERNS) {
        if (pattern.test(cell)) {
          return { valid: false, error: `Potentially unsafe content detected on row ${i + 1}. Please check your file and try again.` };
        }
      }
    }

    // 6 — Email format validation for recipients
    if (validateEmails) {
      const email = lines[i].trim();
      if (email && !EMAIL_REGEX.test(email)) {
        return { valid: false, error: `Invalid email format on row ${i + 1}: "${email}". Please check your file.` };
      }
    }
  }

  return { valid: true };
}

// ─── TOAST ────────────────────────────────────────────────────────────────────
function Toast({ message, type, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2800); return () => clearTimeout(t); }, []);
  const c = {
    success: { bg: T.greenBg, border: "#bbf7d0", text: T.greenText },
    error: { bg: "#fee2e2", border: "#fecaca", text: "#b91c1c" },
    info: { bg: T.blueBg, border: "#bfdbfe", text: T.blueText }
  }[type] || { bg: T.blueBg, border: "#bfdbfe", text: T.blueText };
  return (
    <div style={{
      position: "fixed", bottom: 28, right: 28, zIndex: 9999,
      background: c.bg, border: `1px solid ${c.border}`,
      color: c.text, borderRadius: 10, padding: "12px 20px",
      fontSize: 13.5, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)", animation: "slideUp 0.25s ease",
    }}>{message}</div>
  );
}

// ─── MODAL ────────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 800,
      background: "rgba(14,19,32,0.55)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <div style={{
        background: T.white, borderRadius: 16, width: 460,
        boxShadow: "0 24px 64px rgba(0,0,0,0.2)", overflow: "hidden",
        animation: "popIn 0.2s ease",
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          padding: "20px 24px", borderBottom: `1px solid ${T.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: T.textPrimary, fontFamily: "'Fraunces', serif" }}>{title}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.textMuted, fontSize: 22, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: "24px" }}>{children}</div>
      </div>
    </div>
  );
}

// ─── SIDEBAR ──────────────────────────────────────────────────────────────────
function Sidebar({ screen, onNav, session }) {
  const links = [
    { id: "dashboard", label: "Overview" },
    { id: "reports", label: "Reports" },
    { id: "recipients", label: "Recipients" },
    { id: "companies", label: "Companies" },
  ];
  return (
    <div style={{
      width: 210, minHeight: "100vh", background: T.navy,
      display: "flex", flexDirection: "column", flexShrink: 0,
    }}>
      <div style={{ padding: "28px 24px 22px", borderBottom: `1px solid ${T.navyBorder}` }}>
        <Logo inverted />
      </div>
      <nav style={{ flex: 1, padding: "14px 10px", display: "flex", flexDirection: "column", gap: 2 }}>
        {links.map(l => {
          const active = screen === l.id;
          return (
            <button key={l.id} onClick={() => onNav(l.id)} style={{
              width: "100%", textAlign: "left", padding: "10px 14px",
              borderRadius: 8, border: "none", cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif", fontSize: 13.5,
              fontWeight: active ? 600 : 400,
              background: active ? "rgba(79,142,247,0.14)" : "transparent",
              color: active ? "#a8c8ff" : "rgba(255,255,255,0.42)",
              borderLeft: active ? `3px solid ${T.accent}` : "3px solid transparent",
              transition: "all 0.13s",
            }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.color = "rgba(255,255,255,0.72)"; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.color = "rgba(255,255,255,0.42)"; }}
            >{l.label}</button>
          );
        })}
      </nav>
      <div style={{
        padding: "16px 20px", borderTop: `1px solid ${T.navyBorder}`,
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: "50%",
          background: "linear-gradient(135deg,#4f8ef7,#7c5cbf)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 13, fontWeight: 700, color: T.white, flexShrink: 0,
        }}>{session?.user?.email?.[0]?.toUpperCase() || "U"}</div>
        <span style={{
          fontSize: 12.5, color: "rgba(255,255,255,0.45)", fontFamily: "'DM Sans', sans-serif",
          flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{session?.user?.email || "User"}</span>
        <button onClick={() => supabase.auth.signOut()} style={{
          background: "none", border: "none", cursor: "pointer",
          color: "rgba(255,255,255,0.3)", fontSize: 18, lineHeight: 1,
        }} title="Sign out">→</button>
      </div>
    </div>
  );
}

// ─── PAGE HEADER ──────────────────────────────────────────────────────────────
function PageHeader({ title, action }) {
  return (
    <div style={{
      padding: "22px 32px 18px", borderBottom: `1px solid ${T.border}`,
      background: T.white, display: "flex", justifyContent: "space-between",
      alignItems: "center", flexWrap: "wrap", gap: 12,
    }}>
      <h1 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: T.textPrimary, fontFamily: "'Fraunces', serif", letterSpacing: "-0.4px" }}>{title}</h1>
      {action && <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{action}</div>}
    </div>
  );
}

// ─── NOT ALLOWED SCREEN ───────────────────────────────────────────────────────
function NotAllowed({ email }) {
  return (
    <div style={{
      minHeight: "100vh", background: T.navy, display: "flex",
      alignItems: "center", justifyContent: "center",
      backgroundImage: "radial-gradient(circle at 18% 78%, rgba(79,142,247,0.07) 0%, transparent 50%)",
    }}>
      <div style={{
        background: T.navyMid, borderRadius: 20, padding: "48px 44px", width: 420,
        border: `1px solid ${T.navyBorder}`, boxShadow: "0 32px 80px rgba(0,0,0,0.45)",
        textAlign: "center",
      }}>
        <div style={{ fontSize: 44, marginBottom: 20 }}>🔒</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: T.white, fontFamily: "'Fraunces', serif", marginBottom: 10 }}>
          Access Restricted
        </div>
        <div style={{ fontSize: 13.5, color: "rgba(255,255,255,0.45)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.7, marginBottom: 28 }}>
          <strong style={{ color: "rgba(255,255,255,0.7)" }}>{email}</strong> is not on the
          approved access list. Please contact us to request access.
        </div>
        <a href="mailto:hello@aquityai.com" style={{
          display: "inline-block", padding: "12px 28px",
          background: T.accent, color: T.white, borderRadius: 10,
          fontSize: 14, fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
          textDecoration: "none",
        }}>Contact Us</a>
        <div style={{ marginTop: 20 }}>
          <button onClick={() => supabase.auth.signOut()} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "rgba(255,255,255,0.3)", fontSize: 12.5, fontFamily: "'DM Sans', sans-serif",
          }}>Sign out</button>
        </div>
      </div>
    </div>
  );
}

// ─── AUTH CARD WRAPPER ────────────────────────────────────────────────────────
function AuthCard({ children }) {
  return (
    <div style={{
      minHeight: "100vh", background: T.navy, display: "flex",
      alignItems: "center", justifyContent: "center",
      backgroundImage: "radial-gradient(circle at 18% 78%, rgba(79,142,247,0.07) 0%, transparent 50%)",
    }}>
      <div style={{
        background: T.navyMid, borderRadius: 20, padding: "44px 40px", width: 380,
        border: `1px solid ${T.navyBorder}`, boxShadow: "0 32px 80px rgba(0,0,0,0.45)",
      }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ fontSize: 22, fontFamily: "'Fraunces', serif", fontWeight: 900, color: T.white, letterSpacing: "-0.5px", marginBottom: 8 }}>Aquity AI</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: 2.5, textTransform: "uppercase", fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
            Content Intelligence Agent
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── SIGN IN ──────────────────────────────────────────────────────────────────
function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("login"); // login | signup | check-email | mfa | forgot | reset-sent

  const inputStyle = {
    width: "100%", padding: "12px 14px", border: `1.5px solid ${T.navyBorder}`,
    borderRadius: 10, fontSize: 14, fontFamily: "'DM Sans', sans-serif",
    color: T.white, outline: "none", boxSizing: "border-box",
    background: T.navyLight, transition: "border 0.15s",
  };

  const navyInput = (extraStyle = {}) => ({
    ...inputStyle, ...extraStyle,
  });

  async function checkWhitelist(emailToCheck) {
    const { data, error } = await supabase
      .from("allowed_users")
      .select("email, status")
      .eq("email", emailToCheck.trim().toLowerCase())
      .single();
    if (error || !data) return { allowed: false, reason: "not_found" };
    if (data.status !== "active") return { allowed: false, reason: "inactive" };
    return { allowed: true };
  }

  async function handleSignup() {
    setLoading(true); setErr("");
    const { allowed, reason } = await checkWhitelist(email);
    if (!allowed) {
      setErr(reason === "inactive"
        ? "Your account is not yet active. Please contact us."
        : "This email is not on the approved access list. Please contact us to request access.");
      setLoading(false); return;
    }
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) { setErr(error.message); }
    else { setMode("check-email"); }
    setLoading(false);
  }

  async function handleLogin() {
    setLoading(true); setErr("");
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setErr(error.message); setLoading(false); return; }
    const { allowed, reason } = await checkWhitelist(email);
    if (!allowed) {
      await supabase.auth.signOut();
      setErr(reason === "inactive"
        ? "Your account has been deactivated. Please contact us."
        : "This email is not on the approved access list.");
      setLoading(false); return;
    }
    const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aalData?.nextLevel === "aal2" && aalData?.nextLevel !== aalData?.currentLevel) {
      setMode("mfa");
    }
    setLoading(false);
  }

  async function handleEmail() {
    if (!email || !password) { setErr("Please fill in all fields"); return; }
    if (mode === "signup") { await handleSignup(); }
    else { await handleLogin(); }
  }

  async function handleMfa() {
    if (!token.trim()) { setErr("Please enter the 6-digit code"); return; }
    setLoading(true); setErr("");
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const totpFactor = factors?.totp?.[0];
      if (!totpFactor) { setErr("No MFA factor found. Please contact support."); setLoading(false); return; }
      const { data: challenge } = await supabase.auth.mfa.challenge({ factorId: totpFactor.id });
      const { error } = await supabase.auth.mfa.verify({
        factorId: totpFactor.id, challengeId: challenge.id, code: token,
      });
      if (error) setErr("Invalid code. Please try again.");
    } catch { setErr("Something went wrong. Please try again."); }
    setLoading(false);
  }

  async function handleForgotPassword() {
    if (!resetEmail.trim()) { setErr("Please enter your email address"); return; }
    setLoading(true); setErr("");
    const { error } = await supabase.auth.resetPasswordForEmail(
      resetEmail.trim().toLowerCase(),
      { redirectTo: window.location.origin }
    );
    if (error) { setErr(error.message); }
    else { setMode("reset-sent"); }
    setLoading(false);
  }

  const ErrBox = ({ msg }) => msg ? (
    <div style={{
      fontSize: 12.5, color: "#f87171", marginTop: 10,
      fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5,
      background: "rgba(239,68,68,0.08)", padding: "10px 14px",
      borderRadius: 8, border: "1px solid rgba(239,68,68,0.2)",
    }}>{msg}</div>
  ) : null;

  const BackBtn = ({ label, onClick }) => (
    <div style={{ textAlign: "center", marginTop: 16 }}>
      <button onClick={onClick} style={{
        background: "none", border: "none", cursor: "pointer",
        color: "rgba(255,255,255,0.3)", fontSize: 12.5, fontFamily: "'DM Sans', sans-serif",
      }}>{label}</button>
    </div>
  );

  return (
    <AuthCard>

      {/* ── Check email after signup ── */}
      {mode === "check-email" && (
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>📬</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: T.white, fontFamily: "'Fraunces', serif", marginBottom: 10 }}>
            Check your email
          </div>
          <div style={{ fontSize: 13.5, color: "rgba(255,255,255,0.5)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6, marginBottom: 24 }}>
            We sent a confirmation link to{" "}
            <strong style={{ color: "rgba(255,255,255,0.8)" }}>{email}</strong>.
            Click it to activate your account.
          </div>
          <BackBtn label="Back to sign in" onClick={() => { setMode("login"); setEmail(""); setPassword(""); }} />
        </div>
      )}

      {/* ── Password reset sent ── */}
      {mode === "reset-sent" && (
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>📩</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: T.white, fontFamily: "'Fraunces', serif", marginBottom: 10 }}>
            Reset link sent
          </div>
          <div style={{ fontSize: 13.5, color: "rgba(255,255,255,0.5)", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6, marginBottom: 24 }}>
            We sent a password reset link to{" "}
            <strong style={{ color: "rgba(255,255,255,0.8)" }}>{resetEmail}</strong>.
            Check your inbox and click the link to reset your password.
          </div>
          <BackBtn label="Back to sign in" onClick={() => { setMode("login"); setResetEmail(""); setErr(""); }} />
        </div>
      )}

      {/* ── Forgot password ── */}
      {mode === "forgot" && (
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.white, fontFamily: "'Fraunces', serif", marginBottom: 8 }}>
            Reset your password
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", fontFamily: "'DM Sans', sans-serif", marginBottom: 20, lineHeight: 1.5 }}>
            Enter your email and we'll send you a reset link.
          </div>
          <input
            type="email" placeholder="email@domain.com" value={resetEmail}
            onChange={e => { setResetEmail(e.target.value); setErr(""); }}
            onKeyDown={e => e.key === "Enter" && handleForgotPassword()}
            style={navyInput()}
            onFocus={e => e.target.style.borderColor = T.accent}
            onBlur={e => e.target.style.borderColor = T.navyBorder}
          />
          <ErrBox msg={err} />
          <button onClick={handleForgotPassword} disabled={loading} style={{
            width: "100%", marginTop: 16, padding: "13px",
            background: T.accent, color: T.white, border: "none",
            borderRadius: 10, fontSize: 14, fontWeight: 700,
            fontFamily: "'DM Sans', sans-serif", cursor: "pointer",
            opacity: loading ? 0.7 : 1,
          }}>
            {loading ? "Sending…" : "Send Reset Link"}
          </button>
          <BackBtn label="Back to sign in" onClick={() => { setMode("login"); setErr(""); setResetEmail(""); }} />
        </div>
      )}

      {/* ── MFA ── */}
      {mode === "mfa" && (
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.white, fontFamily: "'Fraunces', serif", marginBottom: 8 }}>
            Two-factor authentication
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", fontFamily: "'DM Sans', sans-serif", marginBottom: 20, lineHeight: 1.5 }}>
            Enter the 6-digit code from your authenticator app.
          </div>
          <input
            type="text" placeholder="000000" value={token} maxLength={6}
            onChange={e => { setToken(e.target.value.replace(/\D/g, "")); setErr(""); }}
            onKeyDown={e => e.key === "Enter" && handleMfa()}
            style={navyInput({ letterSpacing: 8, fontSize: 20, textAlign: "center" })}
            onFocus={e => e.target.style.borderColor = T.accent}
            onBlur={e => e.target.style.borderColor = T.navyBorder}
          />
          <ErrBox msg={err} />
          <button onClick={handleMfa} disabled={loading || token.length !== 6} style={{
            width: "100%", marginTop: 16, padding: "13px",
            background: T.accent, color: T.white, border: "none",
            borderRadius: 10, fontSize: 14, fontWeight: 700,
            fontFamily: "'DM Sans', sans-serif", cursor: "pointer",
            opacity: (loading || token.length !== 6) ? 0.6 : 1,
          }}>
            {loading ? "Verifying…" : "Verify"}
          </button>
          <BackBtn label="Back to sign in" onClick={() => { setMode("login"); setToken(""); setErr(""); }} />
        </div>
      )}

      {/* ── Login / Signup ── */}
      {(mode === "login" || mode === "signup") && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <input type="email" placeholder="email@domain.com" value={email}
              onChange={e => { setEmail(e.target.value); setErr(""); }}
              onKeyDown={e => e.key === "Enter" && handleEmail()}
              style={navyInput()}
              onFocus={e => e.target.style.borderColor = T.accent}
              onBlur={e => e.target.style.borderColor = T.navyBorder}
            />
            <input type="password" placeholder="password" value={password}
              onChange={e => { setPassword(e.target.value); setErr(""); }}
              onKeyDown={e => e.key === "Enter" && handleEmail()}
              style={navyInput()}
              onFocus={e => e.target.style.borderColor = T.accent}
              onBlur={e => e.target.style.borderColor = T.navyBorder}
            />
          </div>

          <ErrBox msg={err} />

          {/* Forgot password link — only on login mode */}
          {mode === "login" && (
            <div style={{ textAlign: "right", marginTop: 8 }}>
              <button onClick={() => { setMode("forgot"); setResetEmail(email); setErr(""); }} style={{
                background: "none", border: "none", cursor: "pointer",
                color: "rgba(255,255,255,0.3)", fontSize: 12, fontFamily: "'DM Sans', sans-serif",
              }}>Forgot password?</button>
            </div>
          )}

          <button onClick={handleEmail} disabled={loading} style={{
            width: "100%", marginTop: 16, padding: "13px",
            background: T.accent, color: T.white, border: "none",
            borderRadius: 10, fontSize: 14, fontWeight: 700,
            fontFamily: "'DM Sans', sans-serif", cursor: "pointer",
            opacity: loading ? 0.7 : 1,
          }}>
            {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Sign up"}
          </button>

          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button onClick={() => { setMode(mode === "login" ? "signup" : "login"); setErr(""); }} style={{
              background: "none", border: "none", cursor: "pointer",
              color: "rgba(255,255,255,0.35)", fontSize: 12.5, fontFamily: "'DM Sans', sans-serif",
            }}>
              {mode === "login" ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        </>
      )}

    </AuthCard>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({ companies, recipients, reports, onNav, showToast, session }) {
  return (
    <div style={{ display: "flex", minHeight: "100vh", background: T.offWhite }}>
      <Sidebar screen="dashboard" onNav={onNav} session={session} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <PageHeader title="Dashboard" action={
          <>
            <Btn variant="outline" onClick={() => onNav("companies")}>+ Add Company</Btn>
            <Btn variant="outline" onClick={() => onNav("recipients")}>+ Add Recipient</Btn>
            {/* <Btn variant="outline" onClick={async () => {
              try {
                await fetch("https://advanceatc.app.n8n.cloud/webhook/b88129aa-57a0-48e1-96d5-e60e3a6b4acd", { method: "POST" });
                showToast("Weekly Adoption Report is running 📊", "success");
              } catch (err) {
                showToast("Failed to trigger report. Check your n8n webhook.", "error");
              }
            }}>▶ Weekly Adoption Report</Btn> */}
            <Btn onClick={async () => {
              try {
                await fetch("https://advanceatc.app.n8n.cloud/webhook/run-aibl3f9a8c2d-91ab", { method: "POST" });
                showToast("Aquity AI Intelligence Scan is running 🚀", "success");
              } catch (err) {
                showToast("Failed to trigger scan. Check your n8n webhook.", "error");
              }
            }}>▶ Run Intelligence Scan</Btn>
          </>
        } />
        <div style={{ flex: 1, padding: "28px 32px", overflowY: "auto" }}>
          <div style={{ display: "flex", gap: 16, marginBottom: 28, flexWrap: "wrap" }}>
            {[
              { label: "Companies Tracked", value: companies.length, click: () => onNav("companies") },
              { label: "Email Recipients", value: recipients.length, click: () => onNav("recipients") },
              { label: "Reports Sent", value: reports.length, click: null },
            ].map((s, i) => (
              <div key={i} onClick={s.click} style={{
                flex: 1, minWidth: 140, background: T.white, border: `1px solid ${T.border}`,
                borderRadius: 14, padding: "22px 24px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                cursor: s.click ? "pointer" : "default", transition: "box-shadow 0.15s, transform 0.15s",
              }}
                onMouseEnter={e => { if (s.click) { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "translateY(-1px)"; }}}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.04)"; e.currentTarget.style.transform = "none"; }}
              >
                <div style={{ fontSize: 10.5, color: T.textMuted, fontFamily: "'DM Sans', sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.1, marginBottom: 10 }}>{s.label}</div>
                <div style={{ fontSize: 44, fontWeight: 900, color: T.navy, fontFamily: "'Fraunces', serif", lineHeight: 1 }}>{s.value}</div>
              </div>
            ))}
          </div>
          <div style={{ background: T.white, borderRadius: 14, border: `1px solid ${T.border}`, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
            <div style={{ padding: "18px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: T.textPrimary, fontFamily: "'Fraunces', serif" }}>Recent Reports</span>
                {/* <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: T.greenText, fontFamily: "'DM Sans', sans-serif", fontWeight: 600 }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: T.green, display: "inline-block", animation: "pulse 2s infinite" }} />
                  Live
                </span> */}
              </div>
              <span style={{ fontSize: 12, color: T.textMuted, fontFamily: "'DM Sans', sans-serif" }}>{reports.length} total</span>
            </div>
            <Table
              headers={["Run Time", "Workflow", "Articles", "Emails Sent", "Status", "Report"]}
              rows={reports.slice(0, 7)}
              emptyMsg="No reports yet. Runs will appear here automatically."
              renderRow={row => (<>
                <Td muted>
                  {row.runTime ? new Date(row.runTime).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                </Td>
                <Td>{row.workflowName}</Td>
                <Td>{row.articlesProcessed ?? '—'}</Td>
                <Td>{row.emailsSent ?? '—'}</Td>
                <Td>
                  <span style={{
                    display: 'inline-block', padding: '3px 10px', borderRadius: 20,
                    fontSize: 11, fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
                    background: row.status === 'success' ? T.greenBg : '#fee2e2',
                    color: row.status === 'success' ? T.greenText : '#b91c1c',
                  }}>{row.status === 'success' ? 'Success' : row.status ?? '—'}</span>
                </Td>
                <Td>
                  <a
                    href={row.reportUrl || undefined}
                    target="_blank"
                    rel="noreferrer"
                    onClick={e => { if (!row.reportUrl) e.preventDefault(); }}
                    style={{
                      display: "inline-block", padding: "4px 12px",
                      borderRadius: 7, fontSize: 12, fontWeight: 600,
                      fontFamily: "'DM Sans', sans-serif",
                      textDecoration: "none",
                      background: row.reportUrl ? T.blueBg : T.offWhite,
                      color: row.reportUrl ? T.blueText : T.textMuted,
                      border: `1px solid ${row.reportUrl ? "#bfdbfe" : T.border}`,
                      cursor: row.reportUrl ? "pointer" : "not-allowed",
                      opacity: row.reportUrl ? 1 : 0.55,
                      pointerEvents: row.reportUrl ? "auto" : "none",
                    }}
                  >
                    {row.reportUrl ? "View Report" : "No Report"}
                  </a>
                </Td>
              </>)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── REPORTS ──────────────────────────────────────────────────────────────────
function Reports({ reports, setReports, onNav, showToast, session }) {
  function formatDate(ts) {
    if (!ts) return '—';
    return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  function formatDuration(secs) {
    if (!secs && secs !== 0) return '—';
    if (secs < 60) return `${secs}s`;
    return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  }
  const totalArticles = reports.reduce((sum, r) => sum + (r.articlesProcessed || 0), 0);
  const totalEmails = reports.reduce((sum, r) => sum + (r.emailsSent || 0), 0);
  const successRate = reports.length ? Math.round((reports.filter(r => r.status === 'success').length / reports.length) * 100) : 0;

  async function handleRefresh() {
    try {
      const data = await reportsApi.getAll();
      setReports(data.map(r => ({
        id: r.id, workflowName: r.workflow_name, runTime: r.run_time,
        articlesProcessed: r.articles_processed, signalsInserted: r.signals_inserted,
        emailsSent: r.emails_sent, status: r.status, durationSeconds: r.duration_seconds,
        reportUrl: r.report_url || null,
      })));
      showToast("Reports refreshed", "success");
    } catch (err) { showToast("Failed to refresh", "error"); }
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: T.offWhite }}>
      <Sidebar screen="reports" onNav={onNav} session={session} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <PageHeader title="Reports" action={
          <Btn variant="outline" onClick={handleRefresh}>↻ Refresh</Btn>
        } />
        <div style={{ flex: 1, padding: '28px 32px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {[
              { label: 'Total Runs', value: reports.length },
              { label: 'Articles Processed', value: totalArticles.toLocaleString() },
              { label: 'Emails Sent', value: totalEmails },
              { label: 'Success Rate', value: `${successRate}%` },
            ].map((s, i) => (
              <div key={i} style={{ flex: 1, minWidth: 120, background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ fontSize: 10.5, color: T.textMuted, fontFamily: "'DM Sans', sans-serif", fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.1, marginBottom: 8 }}>{s.label}</div>
                <div style={{ fontSize: 36, fontWeight: 900, color: T.navy, fontFamily: "'Fraunces', serif", lineHeight: 1 }}>{s.value}</div>
              </div>
            ))}
          </div>
          <div style={{ background: T.white, borderRadius: 14, border: `1px solid ${T.border}`, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: T.textPrimary, fontFamily: "'Fraunces', serif" }}>Workflow Runs</span>
              <span style={{ fontSize: 12, color: T.textMuted, fontFamily: "'DM Sans', sans-serif" }}>{reports.length} total</span>
            </div>
            <Table
              headers={['Run Time', 'Workflow', 'Articles', 'Signals', 'Emails Sent', 'Duration', 'Status', 'Report']}
              rows={reports}
              emptyMsg="No workflow runs yet. Runs will appear here automatically."
              renderRow={row => (<>
                <Td muted>{formatDate(row.runTime)}</Td>
                <Td><span style={{ fontWeight: 600 }}>{row.workflowName}</span></Td>
                <Td>{row.articlesProcessed ?? '—'}</Td>
                <Td>{row.signalsInserted ?? '—'}</Td>
                <Td>{row.emailsSent ?? '—'}</Td>
                <Td muted>{formatDuration(row.durationSeconds)}</Td>
                <Td>
                  <span style={{
                    display: 'inline-block', padding: '3px 10px', borderRadius: 20,
                    fontSize: 11, fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
                    background: row.status === 'success' ? T.greenBg : '#fee2e2',
                    color: row.status === 'success' ? T.greenText : '#b91c1c',
                  }}>{row.status ?? '—'}</span>
                </Td>
                <Td>
                  <a
                    href={row.reportUrl || undefined}
                    target="_blank"
                    rel="noreferrer"
                    onClick={e => { if (!row.reportUrl) e.preventDefault(); }}
                    style={{
                      display: "inline-block", padding: "4px 12px",
                      borderRadius: 7, fontSize: 12, fontWeight: 600,
                      fontFamily: "'DM Sans', sans-serif",
                      textDecoration: "none",
                      background: row.reportUrl ? T.blueBg : T.offWhite,
                      color: row.reportUrl ? T.blueText : T.textMuted,
                      border: `1px solid ${row.reportUrl ? "#bfdbfe" : T.border}`,
                      cursor: row.reportUrl ? "pointer" : "not-allowed",
                      opacity: row.reportUrl ? 1 : 0.55,
                      pointerEvents: row.reportUrl ? "auto" : "none",
                    }}
                  >
                    {row.reportUrl ? "View Report" : "No Report"}
                  </a>
                </Td>
              </>)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── COMPANIES ────────────────────────────────────────────────────────────────
function Companies({ companies, setCompanies, onNav, showToast, session }) {
  const [addCo, setAddCo] = useState(false);
  const [coName, setCoName] = useState('');
  const [coType, setCoType] = useState('');
  const [coWebsite, setCoWebsite] = useState('');
  const [saving, setSaving] = useState(false);

  async function submitCompany() {
    if (!coName.trim()) return;
    setSaving(true);
    try {
      const c = await companiesApi.add(coName.trim(), coType.trim(), coWebsite.trim());
      setCompanies(prev => [{
        id: c.id, name: c.name,
        companyType: c.company_type,
        website: c.website,
        isActive: c.is_active,
      }, ...prev]);
      showToast(`${c.name} added`, 'success');
      setCoName(''); setCoType(''); setCoWebsite(''); setAddCo(false);
    } catch (err) { showToast(err.message, 'error'); }
    finally { setSaving(false); }
  }

  async function removeCompany(id, name) {
    try {
      await companiesApi.remove(id);
      setCompanies(prev => prev.filter(c => c.id !== id));
      showToast(`${name} removed`, 'info');
    } catch (err) { showToast(err.message, 'error'); }
  }

  const label = text => (
    <label style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: T.textSecondary, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.9, fontFamily: "'DM Sans', sans-serif" }}>{text}</label>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: T.offWhite }}>
      <Sidebar screen="companies" onNav={onNav} session={session} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <PageHeader title="Companies" />
        <div style={{ flex: 1, padding: '28px 32px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div style={{ background: T.white, borderRadius: 14, border: `1px solid ${T.border}`, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontSize: 15, fontWeight: 800, color: T.textPrimary, fontFamily: "'Fraunces', serif" }}>Intelligence Companies</span>
                <span style={{ fontSize: 12, color: T.textMuted, fontFamily: "'DM Sans', sans-serif", marginLeft: 8 }}>{companies.length}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="file" accept=".csv" id="companies-csv" style={{ display: "none" }}
                  onChange={async (e) => {
                    const file = e.target.files[0]; if (!file) return;

                    // Security: file-level validation
                    const fileCheck = validateCSV(file);
                    if (!fileCheck.valid) { showToast(fileCheck.error, "error"); e.target.value = ""; return; }

                    const text = await file.text();
                    const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
                    const dataLines = lines.filter(l => !l.toLowerCase().startsWith("name"));
                    if (!dataLines.length) { showToast("No companies found in CSV", "error"); e.target.value = ""; return; }

                    // Security: content-level validation
                    const contentCheck = validateCSVContent(dataLines);
                    if (!contentCheck.valid) { showToast(contentCheck.error, "error"); e.target.value = ""; return; }

                    let added = 0, skipped = 0;
                    for (const line of dataLines) {
                      const [name, company_type, website] = line.split(",").map(v => v?.trim().replace(/^"|"$/g, ""));
                      if (!name) continue;
                      try {
                        const c = await companiesApi.add(name, company_type, website);
                        setCompanies(prev => [{ id: c.id, name: c.name, companyType: c.company_type, website: c.website, isActive: c.is_active }, ...prev]);
                        added++;
                      } catch { skipped++; }
                    }
                    showToast(`${added} imported${skipped ? `, ${skipped} skipped` : ""}`, "success");
                    e.target.value = "";
                  }}
                />
                <Btn variant="outline" small onClick={() => document.getElementById('companies-csv').click()}>Import CSV</Btn>
                <Btn small onClick={() => setAddCo(true)}>+ Add Company</Btn>
              </div>
            </div>
            <Table
              headers={['Company', 'Type', 'Website', '']}
              rows={companies}
              emptyMsg="No companies tracked. Add one above."
              renderRow={row => (<>
                <Td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar name={row.name} />
                    <span style={{ fontWeight: 600 }}>{row.name}</span>
                  </div>
                </Td>
                <Td muted>{row.companyType || '—'}</Td>
                <Td>
                  {row.website
                    ? <a href={row.website.startsWith('http') ? row.website : `https://${row.website}`} target="_blank" rel="noreferrer" style={{ color: T.accent, fontSize: 13, fontFamily: "'DM Sans', sans-serif", textDecoration: "none" }}>{row.website}</a>
                    : <span style={{ color: T.textMuted, fontSize: 13 }}>—</span>
                  }
                </Td>
                <Td><Btn variant="ghost" small onClick={() => removeCompany(row.id, row.name)}>Remove</Btn></Td>
              </>)}
            />
          </div>
        </div>
      </div>
      {addCo && (
        <Modal title="Add Company" onClose={() => setAddCo(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>{label('Company Name *')}<Input placeholder="e.g. Microsoft" value={coName} onChange={e => setCoName(e.target.value)} /></div>
            <div>{label('Company Type')}<Input placeholder="e.g. Enablement Partner" value={coType} onChange={e => setCoType(e.target.value)} /></div>
            <div>{label('Website')}<Input placeholder="e.g. microsoft.com" value={coWebsite} onChange={e => setCoWebsite(e.target.value)} /></div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="outline" onClick={() => setAddCo(false)}>Cancel</Btn>
              <Btn onClick={submitCompany} disabled={!coName.trim() || saving}>{saving ? 'Saving…' : 'Add Company'}</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── RECIPIENTS PAGE ──────────────────────────────────────────────────────────
function RecipientsPage({ recipients, setRecipients, onNav, showToast, session }) {
  const [addRe, setAddRe] = useState(false);
  const [reEmail, setReEmail] = useState("");
  const [saving, setSaving] = useState(false);

  async function submitRecipient() {
    if (!reEmail.trim()) return;
    setSaving(true);
    try {
      const r = await recipientsApi.add(reEmail.trim());
      setRecipients(prev => [{ id: r.id, email: r.email, isActive: r.is_active }, ...prev]);
      showToast(`${r.email} added`, "success");
      setReEmail(""); setAddRe(false);
    } catch (err) { showToast(err.message, "error"); }
    finally { setSaving(false); }
  }

  async function removeRecipient(id, email) {
    try {
      await recipientsApi.remove(id);
      setRecipients(prev => prev.filter(r => r.id !== id));
      showToast(`${email} removed`, "info");
    } catch (err) { showToast(err.message, "error"); }
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: T.offWhite }}>
      <Sidebar screen="recipients" onNav={onNav} session={session} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <PageHeader title="Email Recipients" action={
          <Btn onClick={() => setAddRe(true)}>+ Add Recipient</Btn>
        } />
        <div style={{ flex: 1, padding: "28px 32px", overflowY: "auto" }}>
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            {[
              { label: "Total Recipients", value: recipients.length },
              { label: "Active", value: recipients.filter(r => r.isActive !== false).length },
            ].map((s, i) => (
              <div key={i} style={{ flex: 1, minWidth: 140, background: T.white, border: `1px solid ${T.border}`, borderRadius: 14, padding: "20px 24px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                <div style={{ fontSize: 10.5, color: T.textMuted, fontFamily: "'DM Sans', sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.1, marginBottom: 8 }}>{s.label}</div>
                <div style={{ fontSize: 36, fontWeight: 900, color: T.navy, fontFamily: "'Fraunces', serif", lineHeight: 1 }}>{s.value}</div>
              </div>
            ))}
          </div>
          <div style={{ background: T.white, borderRadius: 14, border: `1px solid ${T.border}`, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
            <div style={{ padding: "18px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: T.textPrimary, fontFamily: "'Fraunces', serif" }}>Recipients</span>
              <div style={{ display: "flex", gap: 8 }}>
                <input type="file" accept=".csv" id="recipients-csv" style={{ display: "none" }}
                  onChange={async (e) => {
                    const file = e.target.files[0]; if (!file) return;

                    // Security: file-level validation
                    const fileCheck = validateCSV(file);
                    if (!fileCheck.valid) { showToast(fileCheck.error, "error"); e.target.value = ""; return; }

                    const text = await file.text();
                    const allLines = text.split("\n").map(l => l.trim()).filter(Boolean);
                    const emails = allLines.filter(l => l.toLowerCase() !== "email" && l.includes("@"));
                    if (!emails.length) { showToast("No valid emails found in CSV", "error"); e.target.value = ""; return; }

                    // Security: content-level validation with email format checking
                    const contentCheck = validateCSVContent(emails, { validateEmails: true });
                    if (!contentCheck.valid) { showToast(contentCheck.error, "error"); e.target.value = ""; return; }

                    let added = 0, skipped = 0;
                    for (const email of emails) {
                      try {
                        const r = await recipientsApi.add(email.toLowerCase());
                        setRecipients(prev => [{ id: r.id, email: r.email, isActive: r.is_active }, ...prev]);
                        added++;
                      } catch { skipped++; }
                    }
                    showToast(`${added} imported${skipped ? `, ${skipped} skipped` : ""}`, "success");
                    e.target.value = "";
                  }}
                />
                <Btn variant="outline" small onClick={() => document.getElementById('recipients-csv').click()}>Import CSV</Btn>
              </div>
            </div>
            <Table
              headers={["Email", "Status", ""]}
              rows={recipients}
              emptyMsg="No recipients yet. Click + Add Recipient to get started."
              renderRow={row => (<>
                <Td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><Avatar name={row.email} round /><span style={{ fontWeight: 600 }}>{row.email}</span></div></Td>
                <Td><span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", background: T.greenBg, color: T.greenText }}>Active</span></Td>
                <Td><Btn variant="ghost" small onClick={() => removeRecipient(row.id, row.email)}>Remove</Btn></Td>
              </>)}
            />
          </div>
        </div>
      </div>
      {addRe && (
        <Modal title="Add Recipient" onClose={() => setAddRe(false)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: T.textSecondary, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.9, fontFamily: "'DM Sans', sans-serif" }}>Email Address *</label>
              <Input type="email" placeholder="e.g. sarah@company.com" value={reEmail} onChange={e => setReEmail(e.target.value)} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Btn variant="outline" onClick={() => setAddRe(false)}>Cancel</Btn>
              <Btn onClick={submitRecipient} disabled={!reEmail.trim() || saving}>{saving ? "Saving…" : "Add Recipient"}</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── APP ROOT ─────────────────────────────────────────────────────────────────
export default function App() {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [accessStatus, setAccessStatus] = useState(null);
  const [screen, setScreen] = useState("dashboard");
  const [companies, setCompanies] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [reports, setReports] = useState([]);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) setAccessStatus(null);
      setAuthLoading(false);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) return;
    async function checkAccess() {
      const email = session.user.email.trim().toLowerCase();
      const { data, error } = await supabase
        .from("allowed_users")
        .select("email, status")
        .eq("email", email)
        .single();
      setAccessStatus(!error && data && data.status === "active" ? "allowed" : "blocked");
    }
    checkAccess();
  }, [session]);

  useEffect(() => {
    if (!session || accessStatus !== "allowed") return;
    companiesApi.getAll()
      .then(data => setCompanies(data.map(c => ({
        id: c.id, name: c.name,
        companyType: c.company_type,
        website: c.website,
        isActive: c.is_active,
      }))))
      .catch(err => showToast(err.message, 'error'));
  }, [session, accessStatus]);

  useEffect(() => {
    if (!session || accessStatus !== "allowed") return;
    recipientsApi.getAll()
      .then(data => setRecipients(data.map(r => ({ id: r.id, email: r.email, isActive: r.is_active }))))
      .catch(err => showToast(err.message, 'error'));
  }, [session, accessStatus]);

  useEffect(() => {
    if (!session || accessStatus !== "allowed") return;
    let isMounted = true;
    async function fetchReports() {
      try {
        const data = await reportsApi.getAll();
        if (!isMounted) return;
        setReports(data.map(r => ({
          id: r.id, workflowName: r.workflow_name, runTime: r.run_time,
          articlesProcessed: r.articles_processed, signalsInserted: r.signals_inserted,
          emailsSent: r.emails_sent, status: r.status, durationSeconds: r.duration_seconds,
          reportUrl: r.report_url || null,
        })));
      } catch (err) { console.error("Reports poll error:", err.message); }
    }
    fetchReports();
    const interval = setInterval(fetchReports, 15000);
    return () => { isMounted = false; clearInterval(interval); };
  }, [session, accessStatus]);

  function showToast(message, type = "info") { setToast({ message, type, key: Date.now() }); }

  const shared = { companies, setCompanies, recipients, setRecipients, reports, setReports, showToast, session };

  if (authLoading) {
    return (
      <div style={{ minHeight: "100vh", background: "#0e1320", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "rgba(255,255,255,0.4)", fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>Loading…</span>
      </div>
    );
  }

  if (!session) return <SignIn />;

  if (session && accessStatus === null) {
    return (
      <div style={{ minHeight: "100vh", background: "#0e1320", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "rgba(255,255,255,0.4)", fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>Verifying access…</span>
      </div>
    );
  }

  if (accessStatus === "blocked") return <NotAllowed email={session.user.email} />;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,800;9..144,900&family=DM+Sans:wght@400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        body { margin: 0; }
        input::placeholder { color: #8f9ab5; }
        @keyframes slideUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes popIn { from { opacity:0; transform:scale(0.95); } to { opacity:1; transform:scale(1); } }
        @keyframes pulse { 0%, 100% { opacity:1; } 50% { opacity:0.3; } }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #d1d9e8; border-radius: 10px; }
      `}</style>

      {screen === "dashboard"  && <Dashboard {...shared} onNav={setScreen} />}
      {screen === "reports"    && <Reports {...shared} onNav={setScreen} />}
      {screen === "companies"  && <Companies {...shared} onNav={setScreen} />}
      {screen === "recipients" && <RecipientsPage {...shared} onNav={setScreen} />}

      {toast && <Toast key={toast.key} message={toast.message} type={toast.type} onDone={() => setToast(null)} />}
    </>
  );
}
